import {FinderAPI} from '@/classes/api/finder.api';
import {SearchEntity, SearchRequestDto} from '@/classes/dto/finder/request/Search.request.dto';
import {ENTITY_BY_KEY, PaletteEntity, PaletteHit, splitPrefix} from '@/components/app/search/searchEntities';
import {canSearchTrainings, runTrainingSearch, TrainingResult} from '@/components/app/search/searchTrainings';

export type SearchGroupResult = { total: number; items: PaletteHit[] };

export type SearchResults = Partial<Record<PaletteEntity, SearchGroupResult>>;

/** Prefix of the trainings area; it is handled by the palette, not by the finder */
const TRAININGS_PREFIX = 'sc';

/**
 * Which sources a term queries: a prefix limits the search to its area
 * ("sc:" only trainings, any other prefix only the finder).
 */
export function searchTargets(term: string): { finder: boolean; trainings: boolean; rest: string } {
    const {prefix, rest} = splitPrefix(term);
    return {
        finder: prefix !== TRAININGS_PREFIX,
        trainings: (!prefix || prefix === TRAININGS_PREFIX) && canSearchTrainings(),
        rest,
    };
}

/**
 * Runs the global search (finder `GET search`). Areas the user may not read are missing in the response;
 * unknown areas are ignored.
 */
export async function runSearch(data: SearchRequestDto): Promise<SearchResults> {
    return (await runSearchWithAcquisition(data)).results;
}

/**
 * Like runSearch, additionally returns the acquisition-only schools (finder `acquisitionInstitutions`). They are only
 * sent with `includeAcquisitionInstitutions` and if fewer than 3 Standorte were found, never inside `institutions`.
 */
export async function runSearchWithAcquisition(data: SearchRequestDto): Promise<{ results: SearchResults; acquisitionInstitutions: SearchGroupResult | null }> {
    const resp = await FinderAPI.search(data);
    const results: SearchResults = {};
    if (!resp || !resp.entities) {
        return {results, acquisitionInstitutions: null};
    }
    for (const key of Object.keys(resp.entities) as SearchEntity[]) {
        const group = resp.entities[key];
        if (ENTITY_BY_KEY[key] && group) {
            results[key] = {total: group.total || 0, items: group.items || []};
        }
    }
    const acquisition = resp.acquisitionInstitutions;
    const acquisitionInstitutions = acquisition && acquisition.items?.length
        ? {total: acquisition.total || 0, items: acquisition.items}
        : null;
    return {results, acquisitionInstitutions};
}

/**
 * Trainings of the term (frontend query to the event-manager, the finder does not know trainings).
 * A failed request yields null so it never fails the whole search.
 */
export async function runTrainingSearchSafe(term: string, status: SearchRequestDto['status'], limit: number): Promise<TrainingResult | null> {
    const targets = searchTargets(term);
    if (!targets.trainings) {
        return null;
    }
    return runTrainingSearch(targets.rest, status || 'active', limit).catch(() => null);
}
